<template>
  <!--
    打刻できる場所（会社の全員に効く場所・プロジェクトの所属メンバーに効く場所・人ごとの場所を何件でも）。

    どこから見えるか：自社設定 → 勤怠計算・打刻 → 下のほう。
    上の設定の［保存する］とは別に、1件ずつその場で保存する（場所は件数が増える。まとめて保存にすると、
    途中で画面を離れたときに全部消える）。
  -->
  <section class="stamping-places" data-testid="stamping-places">
    <h3>{{ T.title }}</h3>
    <p class="stamping-places__lead">{{ T.description }}</p>

    <p v-if="loadFailed" class="stamping-places__error" role="alert">{{ T.loadFailed }}</p>

    <template v-else-if="loaded">
      <p v-if="!places.length" class="stamping-places__empty" data-testid="stamping-places-empty">{{ T.empty }}</p>
      <div v-else class="stamping-places__table">
        <table>
          <thead>
            <tr>
              <th>{{ T.columnName }}</th>
              <th>{{ T.columnTarget }}</th>
              <th>{{ T.columnAddress }}</th>
              <th>{{ T.columnRadius }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="place in places" :key="place.id" :data-testid="'stamping-place-' + place.id">
              <td>{{ place.name }}</td>
              <td>{{ targetOf(place) }}</td>
              <td>
                {{ addressOf(place) }}
                <a :href="mapUrl(place)" target="_blank" rel="noopener noreferrer">地図を表示</a>
              </td>
              <td>{{ place.radius_m }}m</td>
              <td class="stamping-places__actions">
                <button type="button" class="btn btn-sm btn-outline-primary" :disabled="busy" @click="startEdit(place)">{{ T.edit }}</button>
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="busy" @click="remove(place)">{{ T.remove }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        v-if="!editing"
        type="button"
        class="btn btn-outline-primary"
        data-testid="stamping-place-add"
        :disabled="busy"
        @click="startAdd"
      >{{ T.add }}</button>

      <div v-else class="stamping-places__form" data-testid="stamping-place-form">
        <div class="stamping-places__field">
          <label for="stamping-place-name">{{ T.columnName }}</label>
          <input id="stamping-place-name" v-model="draft.name" type="text" maxlength="50" class="form-control"/>
        </div>

        <div class="stamping-places__field">
          <span class="stamping-places__label">{{ T.columnTarget }}</span>
          <label class="stamping-places__radio">
            <input v-model="draft.target" type="radio" value="everyone"/> {{ T.targetEveryone }}
          </label>
          <label class="stamping-places__radio">
            <input v-model="draft.target" type="radio" value="project"/> {{ T.targetProject }}
          </label>
          <label class="stamping-places__radio">
            <input v-model="draft.target" type="radio" value="person"/> {{ T.targetPerson }}
          </label>
          <select
            v-if="draft.target === 'project'"
            v-model="draft.projectId"
            class="form-control"
            data-testid="stamping-place-project"
            :aria-label="T.targetProject"
          >
            <option :value="null" disabled>{{ T.targetProjectPlaceholder }}</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
          </select>
          <select
            v-if="draft.target === 'person'"
            v-model="draft.userId"
            class="form-control"
            data-testid="stamping-place-user"
            :aria-label="T.targetPerson"
          >
            <option :value="null" disabled>{{ T.targetPersonPlaceholder }}</option>
            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
          </select>
        </div>

        <div class="stamping-places__field">
          <label for="stamping-place-address">{{ T.positionLabel }}</label>
          <div class="stamping-places__search">
            <input
              id="stamping-place-address"
              v-model="addressQuery"
              type="text"
              class="form-control"
              :placeholder="T.addressPlaceholder"
              @keydown.enter.prevent="search"
            />
            <button type="button" class="btn btn-outline-primary" :disabled="searching" @click="search">{{ T.searchByAddress }}</button>
            <button type="button" class="btn btn-outline-primary" :disabled="searching" @click="useCurrentLocation">{{ T.useCurrentLocation }}</button>
          </div>
          <p v-if="searchMessage" class="stamping-places__error" role="alert">{{ searchMessage }}</p>
          <ul v-if="candidates.length" class="stamping-places__candidates" data-testid="stamping-place-candidates">
            <li v-for="(candidate, index) in candidates" :key="index">
              <button type="button" class="btn btn-sm btn-outline-primary" @click="choose(candidate)">{{ candidate.title }}</button>
            </li>
          </ul>
          <p class="stamping-places__chosen" data-testid="stamping-place-chosen">
            <template v-if="hasPosition">
              {{ chosenAddress }}
              <a :href="mapUrl(draft)" target="_blank" rel="noopener noreferrer">地図を表示</a>
            </template>
            <template v-else>{{ T.positionNotChosen }}</template>
          </p>
        </div>

        <div class="stamping-places__field">
          <label for="stamping-place-radius">{{ T.radiusLabel }}</label>
          <div class="stamping-places__radius">
            <input id="stamping-place-radius" v-model.number="draft.radius_m" type="number" min="50" max="5000" step="10" class="form-control"/>
            <span>m</span>
          </div>
          <small>{{ T.radiusHint }}</small>
        </div>

        <p v-if="saveMessage" class="stamping-places__error" role="alert" data-testid="stamping-place-error">{{ saveMessage }}</p>
        <div class="stamping-places__buttons">
          <button type="button" class="btn btn-primary" data-testid="stamping-place-save" :disabled="busy" @click="save">{{ T.save }}</button>
          <button type="button" class="btn btn-secondary" :disabled="busy" @click="cancel">{{ T.cancel }}</button>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
import T from '../stampingPlaceTexts'
import { backendErrorMessage } from '@/utils/rosterActualPolicy'
import { stampPlaceName } from '@/utils/stampPlaceName'
import { searchPlacesByAddress } from '@/utils/stampingPlaceSearch'
import { currentStampingLocation } from '@/utils/stampingLocation'

const DEFAULT_RADIUS = 300

const emptyDraft = () => ({
  id: null, name: '', target: 'everyone', userId: null, projectId: null, latitude: null, longitude: null, radius_m: DEFAULT_RADIUS
})

export default {
  name: 'StampingPlaces',

  data () {
    return {
      T,
      loaded: false,
      loadFailed: false,
      busy: false,
      places: [],
      users: [],
      projects: [],
      addresses: {},
      editing: false,
      draft: emptyDraft(),
      addressQuery: '',
      candidates: [],
      searching: false,
      searchMessage: '',
      saveMessage: '',
      chosenAddress: '',
      // 入力の欄を開く・閉じるたびに進める番号。遅れて届いた答え（住所の候補・現在地・住所の名前）が、
      // そのあいだに開き直した別の場所の入力へ混ざらないよう、頼んだときの番号と比べる。
      formGeneration: 0
    }
  },

  computed: {
    hasPosition () {
      return this.draft.latitude != null && this.draft.longitude != null
    }
  },

  created () {
    this.load()
  },

  methods: {
    load () {
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/setting/stamping-places')
        .then(response => {
          const data = (response && response.data) || {}
          this.places = Array.isArray(data.places) ? data.places : []
          this.users = Array.isArray(data.users) ? data.users : []
          this.projects = Array.isArray(data.projects) ? data.projects : []
          this.loaded = true
          this.loadFailed = false
          this.places.forEach(place => this.resolveAddress(place))
        })
        .catch(() => {
          this.loadFailed = true
        })
    },
    keyOf (place) {
      return place.latitude + ',' + place.longitude
    },
    resolveAddress (place) {
      const key = this.keyOf(place)
      if (Object.prototype.hasOwnProperty.call(this.addresses, key)) return
      stampPlaceName(place.latitude, place.longitude)
        .then(name => { this.$set(this.addresses, key, name || '') })
    },
    addressOf (place) {
      const name = this.addresses[this.keyOf(place)]
      return name ? name + ' 付近' : ''
    },
    /** 一覧の「利用できる社員」：全員／プロジェクト：名前／社員の名前。 */
    targetOf (place) {
      if (place.user_id != null) return place.user_name || ''
      if (place.project_id != null) return T.targetProjectNamed.replace('{名前}', place.project_name || '')
      return T.targetEveryone
    },
    mapUrl (place) {
      return 'https://www.google.com/maps?q=' + Number(place.latitude) + ',' + Number(place.longitude)
    },
    startAdd () {
      this.draft = emptyDraft()
      this.openForm('')
    },
    startEdit (place) {
      this.draft = {
        id: place.id,
        name: place.name,
        target: place.user_id != null ? 'person' : (place.project_id != null ? 'project' : 'everyone'),
        userId: place.user_id == null ? null : place.user_id,
        projectId: place.project_id == null ? null : place.project_id,
        latitude: Number(place.latitude),
        longitude: Number(place.longitude),
        radius_m: place.radius_m
      }
      this.openForm(this.addressOf(place))
    },
    openForm (address) {
      this.formGeneration += 1
      this.searching = false
      this.editing = true
      this.addressQuery = ''
      this.candidates = []
      this.searchMessage = ''
      this.saveMessage = ''
      this.chosenAddress = address
    },
    cancel () {
      this.formGeneration += 1
      this.searching = false
      this.editing = false
      this.saveMessage = ''
    },
    setPosition (latitude, longitude, address) {
      this.draft.latitude = latitude
      this.draft.longitude = longitude
      this.chosenAddress = address || ''
      if (!address) {
        const generation = this.formGeneration
        stampPlaceName(latitude, longitude).then(name => {
          if (generation !== this.formGeneration) return
          if (this.draft.latitude === latitude && this.draft.longitude === longitude) {
            this.chosenAddress = name ? name + ' 付近' : ''
          }
        })
      }
    },
    search () {
      this.searching = true
      this.searchMessage = ''
      this.candidates = []
      const generation = this.formGeneration
      return searchPlacesByAddress(this.addressQuery)
        .then(candidates => {
          if (generation !== this.formGeneration) return
          this.candidates = candidates
          if (!candidates.length) this.searchMessage = T.addressNotFound
          this.searching = false
        })
    },
    choose (candidate) {
      this.setPosition(candidate.latitude, candidate.longitude, candidate.title)
      this.candidates = []
    },
    useCurrentLocation () {
      this.searching = true
      this.searchMessage = ''
      this.candidates = []
      const generation = this.formGeneration
      return currentStampingLocation()
        .then(location => {
          // 待つあいだに入力をやめた・別の場所を開いたなら、その答えは捨てる
          // （前の場所のつもりで取った現在地が、いま開いている場所の位置になってしまう）。
          if (generation !== this.formGeneration) return
          if (location.location_status === 'OK') {
            this.setPosition(location.latitude, location.longitude, '')
          } else {
            this.searchMessage = T.currentLocationFailed
          }
          this.searching = false
        })
    },
    save () {
      this.saveMessage = ''
      // 「指定した社員のみ」を選んだのに社員を選んでいない保存は断る。
      // 通すと、その人だけのつもりの場所（自宅など）が、黙って全員の場所として保存される。
      if (this.draft.target === 'person' && this.draft.userId == null) {
        this.saveMessage = T.targetPersonPlaceholder
        return Promise.resolve()
      }
      // プロジェクトを選んでいない「指定したプロジェクトの社員」も、同じ理由で断る。
      if (this.draft.target === 'project' && this.draft.projectId == null) {
        this.saveMessage = T.targetProjectPlaceholder
        return Promise.resolve()
      }
      if (!this.hasPosition) {
        this.saveMessage = T.positionNotChosen
        return Promise.resolve()
      }
      this.busy = true
      return axios.post(process.env.VUE_APP_API_ORIGIN + '/setting/stamping-places/save', {
        id: this.draft.id,
        // 誰に効く場所かを明示して送る。送らない保存は、この項目を知らない古い画面からの保存として扱われ、
        // プロジェクト向けの場所の対象が保たれる（全員の場所に変わらない）。
        target: this.draft.target === 'person' ? 'PERSON' : (this.draft.target === 'project' ? 'PROJECT' : 'EVERYONE'),
        user_id: this.draft.target === 'person' ? this.draft.userId : null,
        project_id: this.draft.target === 'project' ? this.draft.projectId : null,
        name: this.draft.name,
        latitude: this.draft.latitude,
        longitude: this.draft.longitude,
        radius_m: this.draft.radius_m
      })
        .then(() => {
          this.formGeneration += 1
          this.editing = false
          return this.load()
        })
        .catch(error => {
          this.saveMessage = backendErrorMessage(error, T.loadFailed)
        })
        .then(() => { this.busy = false })
    },
    remove (place) {
      if (!window.confirm(T.removeConfirm.replace('{name}', place.name))) return Promise.resolve()
      this.busy = true
      return axios.post(process.env.VUE_APP_API_ORIGIN + '/setting/stamping-places/delete', { id: place.id })
        .then(() => this.load())
        .catch(error => { window.alert(backendErrorMessage(error, T.loadFailed)) })
        .then(() => { this.busy = false })
    }
  }
}
</script>

<style scoped>
.stamping-places {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #d8dbe0;
}
.stamping-places h3 {
  font-size: 1rem;
  font-weight: 700;
}
.stamping-places__lead,
.stamping-places small {
  color: #5c6873;
}
.stamping-places__empty {
  color: #5c6873;
}
.stamping-places__error {
  margin: 0.4rem 0;
  color: #c0392b;
  font-weight: 700;
}
.stamping-places__table {
  overflow-x: auto;
  margin-bottom: 0.75rem;
}
.stamping-places__table table {
  width: 100%;
  border-collapse: collapse;
}
.stamping-places__table th,
.stamping-places__table td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #e4e7ea;
  text-align: left;
  vertical-align: middle;
}
.stamping-places__actions {
  white-space: nowrap;
}
.stamping-places__actions button + button {
  margin-left: 0.4rem;
}
.stamping-places__form {
  max-width: 44rem;
  padding: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.4rem;
  background: #f8f9fa;
}
.stamping-places__field {
  margin-bottom: 0.9rem;
}
.stamping-places__field > label,
.stamping-places__label {
  display: block;
  font-weight: 600;
}
.stamping-places__radio {
  display: inline-block;
  margin-right: 1.2rem;
  font-weight: 400;
}
.stamping-places__search,
.stamping-places__radius,
.stamping-places__buttons {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.stamping-places__search input {
  flex: 1 1 14rem;
}
.stamping-places__radius input {
  max-width: 8rem;
}
.stamping-places__candidates {
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}
.stamping-places__candidates li {
  margin-bottom: 0.3rem;
}
.stamping-places__chosen {
  margin: 0.5rem 0 0;
}
</style>
